import { BASE_URL } from "../../../features/api/apiSlice";
import {
  STATUS_TABS,
  chapterState,
  countsByTab,
  ENDORSE_LABELS,
  fmtDate,
  isFullyEndorsed,
  panelOf,
  refereeCell,
  tabLabel,
  type StatusKey,
} from "./upgradeStatus";

/**
 * Exports of the upgrade-applications list, in the two forms the secretariat
 * actually files things in: a spreadsheet to work from, and a document to sign
 * and put in front of a committee.
 *
 * Both are built from the SAME rows the table is showing and filed by the same
 * panelOf() the tabs use, so an export can never disagree with the screen it
 * was taken from. Each carries a header naming the category, the panel and the
 * filter, plus the full status breakdown — a listing with no statement of what
 * it is a listing OF is worthless a week later.
 *
 * Deliberately dependency-free. The portal ships no spreadsheet or PDF library,
 * and adding one to print a table is not a trade worth making:
 *   · the spreadsheet is CSV with a UTF-8 BOM, which Excel opens directly
 *     (without the "format and extension don't match" warning an HTML .xls
 *     produces) and which renders ₦ and accented names correctly;
 *   · the PDF is a styled print window — the same idiom the receipts and
 *     access cards already use — where the browser's own "Save as PDF" does
 *     the writing.
 */

export type ExportMeta = {
  /** The category being upgraded TO, e.g. "Fellow". */
  category: string;
  /** Which panel was open when the export was taken. */
  tab: StatusKey;
  /** The search box's contents, if any. */
  query?: string;
  /**
   * The sitting these rows were taken from, when the export comes off a review
   * cycle rather than off a category list.
   *
   * A listing has to say what it is a listing OF, and "Upgrade Applications to
   * Member" is the wrong answer for one cycle's work — the whole point of the
   * cycle is that it is a subset a named panel decided. So it is named in the
   * heading, in the subtitle line and in the filename rather than left for the
   * reader to infer from the row count.
   */
  cycle?: string;
  /**
   * The decision filter the screen had up, when one was on — "Recommended",
   * "Awaiting a decision", and so on.
   *
   * A roll of 40 taken off a cycle of 175 with nothing saying which 40 is the
   * kind of thing that gets signed and then quoted back as the sitting's whole
   * output. Named here, so it is on the face of both exports and in the
   * filename, rather than left to whoever opens the file to work out.
   */
  filter?: string;
};

/** The document's own title — the category, or the sitting it was taken from. */
function heading(meta: ExportMeta): string {
  return meta.cycle
    ? `Upgrade Review Cycle — ${meta.cycle}`
    : `Upgrade Applications to ${meta.category}`;
}

/**
 * Where an uploaded file lives. Paths are stored relative to the public disk,
 * so the export has to resolve them to something that opens outside the portal
 * — a CSV row reading "profile_pics/x.jpg" is of no use to anyone.
 *
 * Derived from BASE_URL rather than hard-coded, so a staging build exports
 * staging's photographs rather than production's.
 */
const STORAGE_ROOT = `${BASE_URL.replace(/\/api\/?$/, "")}/storage`;

export function resolveUploadUrl(path?: string | null): string {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${STORAGE_ROOT}/${String(path).replace(/^\/+/, "")}`;
}

/**
 * The applicant's photograph: the one attached to THIS application first, then
 * the one on their account.
 *
 * In that order because the application's photograph is the one they submitted
 * for this upgrade, and it is what the induction paperwork is checked against —
 * an account avatar may be years old or absent entirely.
 */
export function applicantPhotoUrl(r: any): string {
  return resolveUploadUrl(r?.passport_photo || r?.pro_pic || r?.user?.pro_pic);
}

/**
 * The photograph the APPLICATION carries — the same chain as
 * `applicantPhotoUrl`, and deliberately so.
 *
 * `upgrade_applications.pro_pic` / `.passport_photo` are only written when the
 * applicant uploads a NEW file on step 1 of the wizard; otherwise the column
 * stays null and the form simply shows the photograph it was prefilled with,
 * from the account. So the account photograph IS the picture attached to the
 * application in that case, and insisting on the row's own column finds nothing
 * for the majority of applicants — while the PDF export, which falls back, shows
 * a face for every one of them.
 */
export function applicationPhotoUrl(r: any): string {
  return applicantPhotoUrl(r);
}

/**
 * A number to call the applicant on.
 *
 * The application's own first, then the register's, then the account's. In that
 * order because `upgrade_applications.phone` is what the applicant typed on
 * step 1 of THIS application — the number they expect to be reached on about it
 * — while the register's may be years old. Both fallbacks are kept because the
 * column is only filled from the wizard, and an older application can have it
 * empty; a blank cell in a chase list is the one thing this column must not be.
 */
export function applicantPhone(r: any): string {
  const number =
    r?.phone ||
    r?.user?.member?.phone ||
    r?.user?.member?.phone2 ||
    r?.user?.phone_number;

  return number ? String(number).trim() : "";
}

/**
 * A date out of the register — the registration date, the date a probationer
 * was admitted — written as "2 Mar 2015".
 *
 * Not fmtDate(), for two reasons.
 *
 * These columns arrive as DATE-ONLY strings, and `new Date("2015-03-02")` is
 * parsed as UTC midnight: on a machine set to any timezone behind UTC that
 * prints the day before. A seniority date is not a thing to be a day out on,
 * and these are read to settle who was admitted first.
 *
 * And an absent date is left BLANK rather than "—". Plenty of these are
 * legitimately empty — a member who never passed through probationer has no
 * probationer date — and an em dash in a date column is text to Excel, which
 * stops the column sorting as dates.
 */
export function fmtRegisterDate(value?: string | null): string {
  if (!value) return "";

  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));

  if (!parts) {
    const formatted = fmtDate(value);
    return formatted === "—" ? "" : formatted;
  }

  const [, year, month, day] = parts;

  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * The photograph on the applicant's account, and only that.
 *
 * No fallback the other way: this slot exists so the submitted photograph can be
 * checked against the account's, and filling it with the application's upload
 * would make every pair match by construction.
 */
export function profilePhotoUrl(r: any): string {
  return resolveUploadUrl(r?.user?.pro_pic);
}

/** One row of the export, in the order the columns are written. */
const COLUMNS = [
  "Member",
  "Membership No",
  // The number issued on upgrade to Member (members.member_member_no). Blank
  // until the batch has been issued, which is itself the useful reading: this
  // column is how the secretariat checks the issue worked.
  "New Members Number",
  "Photo",
  "Email",
  // The list is worked from as a chase list — endorsements, interview times,
  // unpaid fees — and every one of those chases is a phone call.
  "Phone",
  /*
   * The register's own dates, which the application does not carry.
   *
   * "Member Reg Date" is `members.reg_date`, when the Institute first
   * registered them; "Probationer Date" is when they were admitted as a
   * probationer. Both are read as SENIORITY — an upgrade list is worked
   * through by how long somebody has been on the register, and until now that
   * meant opening each application to find out.
   */
  "Member Reg Date",
  "Probationer Date",
  "From",
  "Route",
  "Fee",
  /*
   * CPD, as three figures rather than one.
   *
   * "CPD Points" is the ledger: earned, allocated, final. "CPD Claimed
   * (Unallocated)" is what the member has claimed and nobody has approved yet —
   * self-declared activities still being processed, worth nothing until the
   * certificate behind each is checked. "CPD Total" is the two added, and is
   * the figure the portal actually let them pay on.
   *
   * The gap between the first and the third is what the sheet exists to show:
   * an application can arrive for review resting on claims nobody has looked
   * at, and a claim shrinks on approval or vanishes on rejection.
   */
  "CPD Points",
  "CPD Claimed (Unallocated)",
  "CPD Total",
  "Chapter",
  "Chapter Endorsement",
  "Referee 1",
  "Referee 1 Decision",
  "Referee 2",
  "Referee 2 Decision",
  "Fully Endorsed",
  "Status",
  "Filed Under",
  "Committee Recommendations",
  "Submitted",
] as const;

function toRow(r: any): string[] {
  const ref1 = refereeCell(r, 0);
  const ref2 = refereeCell(r, 1);

  return [
    r.user?.name ?? "—",
    r.user?.member?.membership_no ?? "",
    r.user?.member?.member_member_no ?? "",
    applicantPhotoUrl(r),
    r.user?.email ?? "",
    applicantPhone(r),
    fmtRegisterDate(r.user?.member?.reg_date),
    fmtRegisterDate(r.user?.member?.probationer_date),
    r.from_category?.name ?? "—",
    r.route ?? "",
    // Amounts are stored in kobo; the table divides by 100 and so must this.
    r.amount != null ? String(Number(r.amount) / 100) : "",
    // Plain numbers, not formatted: these columns get added up and sorted in
    // the spreadsheet, and "12.5 pts" is text to Excel. A row the server has
    // not costed yet is left blank rather than written as 0 — nothing counted
    // and nothing earned are not the same claim to make about somebody.
    r.cpd_awarded != null ? String(r.cpd_awarded) : "",
    r.cpd_claimed_unallocated != null ? String(r.cpd_claimed_unallocated) : "",
    r.cpd_effective != null ? String(r.cpd_effective) : "",
    r.chapter?.name ?? r.chapter_other ?? "",
    ENDORSE_LABELS[chapterState(r)],
    ref1.name ?? "",
    ref1.name ? ENDORSE_LABELS[ref1.state] : "",
    ref2.name ?? "",
    ref2.name ? ENDORSE_LABELS[ref2.state] : "",
    isFullyEndorsed(r) ? "Yes" : "No",
    String(r.status ?? "").replace(/_/g, " "),
    tabLabel(panelOf(r)),
    String(r.committee_approved_count ?? 0),
    fmtDate(r.created_at),
  ];
}

/** Today, for the "taken on" line and the filename. */
export function stamp() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    printed: now.toLocaleString("en-GB", {
      day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
    }),
    file: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
  };
}

export function slug(text: string) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "export";
}

function filename(meta: ExportMeta, extension: string) {
  // The decision filter rides in the name too: two exports of one cycle taken
  // minutes apart are otherwise distinguishable only by their timestamps.
  const narrowed = meta.filter?.trim() ? `-${slug(meta.filter)}` : "";

  return meta.cycle
    ? `upgrade-cycle-${slug(meta.cycle)}-${slug(tabLabel(meta.tab))}${narrowed}-${stamp().file}.${extension}`
    : `upgrades-${slug(meta.category)}-${slug(tabLabel(meta.tab))}${narrowed}-${stamp().file}.${extension}`;
}

/** Every status and its count, for the report block both exports carry. */
function breakdown(allRows: any[]) {
  const counts = countsByTab(allRows);
  return STATUS_TABS.map((t) => ({ label: t.label, count: counts.get(t.key) || 0 }));
}

/* ── Spreadsheet ─────────────────────────────────────────────────────────── */

/** RFC 4180: quote everything, and double any quote inside a field. */
const csvCell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const csvLine = (cells: unknown[]) => cells.map(csvCell).join(",");

/**
 * A phone number that survives being opened in Excel.
 *
 * Quoting is not enough: Excel reads "08031234567" as the NUMBER 8031234567 and
 * drops the leading zero, leaving a column of ten-digit numbers nobody can
 * dial. The ="…" form is the one escape both Excel and Google Sheets honour as
 * text. Applied by COLUMN rather than inside toRow(), because toRow() is shared
 * with the PDF — which would otherwise print the escape instead of the number.
 */
const csvPhoneCell = (value: unknown) => {
  const number = String(value ?? "").trim();
  return number ? `"=""${number.replace(/"/g, '""')}"""` : '""';
};

/** Looked up, not hard-coded: a column inserted ahead of it must not shift it. */
const PHONE_COL = COLUMNS.indexOf("Phone");

/** Columns the printed sheet must not break across two lines. */
const NOWRAP_COLS = [
  COLUMNS.indexOf("Member Reg Date"),
  COLUMNS.indexOf("Probationer Date"),
  COLUMNS.indexOf("Submitted"),
];

const csvDataLine = (cells: unknown[]) =>
  cells.map((cell, i) => (i === PHONE_COL ? csvPhoneCell(cell) : csvCell(cell))).join(",");

export function exportUpgradesSpreadsheet(rows: any[], allRows: any[], meta: ExportMeta) {
  const { printed } = stamp();

  const lines: string[] = [
    csvLine([`NIQS — ${heading(meta)}`]),
    ...(meta.cycle && meta.category ? [csvLine([`Grade: ${meta.category}`])] : []),
    csvLine([`Panel: ${tabLabel(meta.tab)}`]),
    ...(meta.filter?.trim() ? [csvLine([`Showing: ${meta.filter.trim()}`])] : []),
    ...(meta.query?.trim() ? [csvLine([`Search: ${meta.query.trim()}`])] : []),
    csvLine([`Applications listed: ${rows.length}`]),
    csvLine([`Taken on: ${printed}`]),
    "",
    csvLine([`Status breakdown (${meta.cycle ? "whole cycle" : "whole category"})`]),
    ...breakdown(allRows).map((b) => csvLine([b.label, b.count])),
    "",
    csvLine([...COLUMNS]),
    ...rows.map((r) => csvDataLine(toRow(r))),
  ];

  // The BOM is what makes Excel read this as UTF-8 rather than the local
  // codepage — without it ₦ and any accented name arrive as mojibake.
  const blob = new Blob(["﻿" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename(meta, "csv");
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick — Safari cancels the download if the URL dies
  // before it has read it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ── PDF (via the browser's print dialog) ────────────────────────────────── */

const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export function exportUpgradesPdf(rows: any[], allRows: any[], meta: ExportMeta) {
  const { printed } = stamp();
  const w = window.open("", "_blank", "width=1100,height=800");
  if (!w) {
    alert("Allow pop-ups for this site to export a PDF.");
    return;
  }

  const summary = breakdown(allRows)
    .map(
      (b) => `<div class="stat"><span class="n">${b.count}</span><span class="l">${esc(b.label)}</span></div>`,
    )
    .join("");

  // Looked up rather than hard-coded: adding a column ahead of these used to
  // silently move the naira formatting onto whatever landed in the old slot.
  const FEE_COL = COLUMNS.indexOf("Fee");
  const PHOTO_COL = COLUMNS.indexOf("Photo");

  const cellHtml = (cell: string, i: number) => {
    // The photograph is printed, not linked — this document is signed and filed,
    // and a URL on paper is not a face.
    if (i === PHOTO_COL) {
      return cell
        ? `<td class="photo"><img src="${esc(cell)}" alt="" /></td>`
        : `<td class="photo"><span class="nophoto">—</span></td>`;
    }

    // A number broken across two lines on paper is a number that gets
    // misdialled, and a date broken across two is one that gets misread.
    if (i === PHONE_COL || NOWRAP_COLS.includes(i)) {
      return `<td class="tel">${esc(cell)}</td>`;
    }

    if (i === FEE_COL && cell) {
      return `<td class="num">&#8358;${Number(cell).toLocaleString("en-NG")}</td>`;
    }

    return `<td>${esc(cell)}</td>`;
  };

  const body = rows.length
    ? rows
        .map(
          (r) => `<tr>${toRow(r)
            .map(cellHtml)
            .join("")}</tr>`,
        )
        .join("")
    : `<tr><td class="empty" colspan="${COLUMNS.length}">No applications in this panel.</td></tr>`;

  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(filename(meta, "pdf").replace(/\.pdf$/, ""))}</title>
  <style>
    @page{size:A4 landscape;margin:12mm}
    *{box-sizing:border-box;margin:0;padding:0;font-family:'Segoe UI',Arial,sans-serif}
    body{color:#111;font-size:10px}
    .head{border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:14px}
    .org{font-size:9px;font-weight:900;letter-spacing:.22em;text-transform:uppercase;color:#888}
    h1{font-size:17px;font-weight:900;margin-top:4px}
    .sub{font-size:10px;color:#666;margin-top:5px;line-height:1.6}
    .sub b{color:#111}
    .stats{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:14px}
    .stat{border:1px solid #e6e6e6;border-radius:7px;padding:6px 10px;min-width:78px}
    .stat .n{display:block;font-size:15px;font-weight:900}
    .stat .l{display:block;font-size:8px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#999;margin-top:1px}
    table{width:100%;border-collapse:collapse}
    th{background:#111;color:#fff;font-size:8px;font-weight:900;letter-spacing:.08em;text-transform:uppercase;text-align:left;padding:7px 6px}
    td{padding:6px;border-bottom:1px solid #eee;vertical-align:top}
    td.num{white-space:nowrap;text-align:right}
    td.tel{white-space:nowrap}
    td.empty{text-align:center;color:#999;padding:24px}
    /* Browsers drop background and image colour from printouts unless told not
       to. A photograph printed as a grey box defeats the point of carrying it. */
    td.photo{width:44px;padding:4px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    td.photo img{width:38px;height:38px;object-fit:cover;border-radius:4px;border:1px solid #ddd;display:block}
    td.photo .nophoto{color:#ccc}
    tr{page-break-inside:avoid}
    thead{display:table-header-group}
    .foot{margin-top:14px;padding-top:8px;border-top:1px solid #eee;font-size:8px;color:#aaa;font-weight:800;letter-spacing:.12em;text-transform:uppercase}
  </style>
</head>
<body>
  <div class="head">
    <div class="org">Nigerian Institute of Quantity Surveyors</div>
    <h1>${esc(heading(meta))}</h1>
    <div class="sub">
      ${meta.cycle && meta.category ? `Grade: <b>${esc(meta.category)}</b> &middot; ` : ""}Panel: <b>${esc(tabLabel(meta.tab))}</b>${
        meta.filter?.trim() ? ` &middot; Showing: <b>${esc(meta.filter.trim())}</b>` : ""
      } &middot; ${rows.length} application(s) listed${
        meta.query?.trim() ? ` &middot; filtered by &ldquo;${esc(meta.query.trim())}&rdquo;` : ""
      }<br>
      Taken on ${esc(printed)}
    </div>
  </div>

  <div class="stats">${summary}</div>

  <table>
    <thead><tr>${COLUMNS.map((c) => `<th>${esc(c)}</th>`).join("")}</tr></thead>
    <tbody>${body}</tbody>
  </table>

  <div class="foot">NIQS Portal &middot; Upgrade Applications Report</div>
  <!-- onload rather than an immediate call, so the photographs are on the page
       before the print dialog snapshots it. The timeout is the fallback for a
       photograph that never loads — a hung image must not cost the whole
       document. Guarded so the two paths cannot both print. -->
  <script>
    (function () {
      var printed = false;
      function go() { if (printed) return; printed = true; window.print(); }
      window.onload = go;
      setTimeout(go, 6000);
    })();
  </script>
</body>
</html>`);
  w.document.close();
}
